import React from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import SEOMeta from '../components/SEOMeta';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-serif text-xl font-semibold text-[var(--color-deep)] mb-3 pb-2 border-b border-[var(--color-border)]">{title}</h2>
    <div className="space-y-3 text-sm text-[var(--color-muted-text)] leading-relaxed">{children}</div>
  </div>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Privacy Policy"
        description="Informativa sul trattamento dei dati personali ai sensi del GDPR (Reg. UE 2016/679) di Una Zampa In Paradiso S.r.l.s."
        url="/privacy"
      />

      {/* Hero */}
      <section className="bg-[var(--color-deep)] py-14">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <Shield size={13} /> Documento Legale
            </div>
            <h1 className="font-serif text-4xl font-semibold text-white mb-2">Privacy Policy</h1>
            <p className="text-white/60 text-sm">Informativa ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR)</p>
            <p className="text-white/40 text-xs mt-2">Ultimo aggiornamento: giugno 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Contenuto */}
      <section className="section-py">
        <div className="container-custom max-w-3xl">
          <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8 shadow-sm">

            <Section title="1. Titolare del Trattamento">
              <p>
                Il Titolare del trattamento dei dati personali è:<br />
                <strong className="text-[var(--color-ink)]">Una Zampa In Paradiso S.r.l.s.</strong><br />
                Via Mangiacane, 11 — 12011 Borgo San Dalmazzo (CN), Italia<br />
                P.IVA: 04183140047<br />
                E-mail: <a href="mailto:privacy@unazampainparadiso.com" className="text-[var(--color-sage)] hover:underline">privacy@unazampainparadiso.com</a>
              </p>
            </Section>

            <Section title="2. Tipologie di Dati Raccolti">
              <p>Il portale raccoglie le seguenti categorie di dati personali:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Dati di registrazione:</strong> nome, cognome, indirizzo e-mail, password (cifrata).</li>
                <li><strong>Dati del profilo cliente:</strong> indirizzo, numero di telefono, informazioni sull'animale (nome, specie, data di nascita e decesso).</li>
                <li><strong>Dati del Memorial:</strong> descrizione dell'animale, foto caricate dall'utente, messaggi di ricordo.</li>
                <li><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, pagine visitate, cookie tecnici.</li>
                <li><strong>Dati della newsletter:</strong> nome e indirizzo e-mail per l'iscrizione volontaria.</li>
                <li><strong>Dati di contatto:</strong> informazioni inserite nel modulo di contatto o di preventivo.</li>
              </ul>
            </Section>

            <Section title="3. Finalità e Base Giuridica del Trattamento">
              <p>I dati personali sono trattati per le seguenti finalità:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Erogazione dei servizi (art. 6.1.b GDPR):</strong> creazione e gestione dell'account, prenotazione di servizi funerari, gestione dei memorial nel Giardino delle Stelle.</li>
                <li><strong>Adempimenti legali (art. 6.1.c GDPR):</strong> obblighi fiscali, contabili e normativi.</li>
                <li><strong>Interesse legittimo (art. 6.1.f GDPR):</strong> sicurezza del portale, prevenzione di abusi, statistiche aggregate anonime.</li>
                <li><strong>Consenso (art. 6.1.a GDPR):</strong> invio della newsletter mensile, utilizzo di cookie non tecnici.</li>
              </ul>
            </Section>

            <Section title="4. Modalità di Trattamento e Conservazione">
              <p>I dati sono trattati con strumenti informatici nel rispetto delle misure di sicurezza previste dal GDPR. Le password sono memorizzate esclusivamente in forma cifrata (bcrypt). Le foto caricate sono conservate su server sicuri.</p>
              <p><strong className="text-[var(--color-ink)]">Tempi di conservazione:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Dati account: per tutta la durata del rapporto contrattuale + 10 anni (obblighi fiscali).</li>
                <li>Dati newsletter: fino alla revoca del consenso.</li>
                <li>Log di navigazione: massimo 12 mesi.</li>
                <li>Richieste di preventivo: 5 anni dall'ultima interazione.</li>
              </ul>
            </Section>

            <Section title="5. Comunicazione e Diffusione dei Dati">
              <p>I dati non vengono ceduti, venduti o diffusi a terzi. Possono essere comunicati a:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Fornitori di servizi tecnici (hosting, e-mail) operanti come Responsabili del Trattamento.</li>
                <li>Autorità giudiziarie o amministrative, nei casi previsti dalla legge.</li>
              </ul>
              <p>I dati sono conservati su server ubicati nell'Unione Europea.</p>
            </Section>

            <Section title="6. Diritti dell'Interessato">
              <p>Ai sensi degli artt. 15-22 del GDPR, l'utente ha diritto di:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Accesso (art. 15):</strong> ottenere conferma del trattamento e copia dei dati.</li>
                <li><strong>Rettifica (art. 16):</strong> correggere dati inesatti o incompleti.</li>
                <li><strong>Cancellazione (art. 17):</strong> richiedere la cancellazione ("diritto all'oblio").</li>
                <li><strong>Limitazione (art. 18):</strong> limitare il trattamento in determinate circostanze.</li>
                <li><strong>Portabilità (art. 20):</strong> ricevere i propri dati in formato strutturato e leggibile.</li>
                <li><strong>Opposizione (art. 21):</strong> opporsi al trattamento per motivi legittimi.</li>
                <li><strong>Revoca del consenso:</strong> in qualsiasi momento, senza pregiudizio per la liceità del trattamento precedente.</li>
              </ul>
              <p>Per esercitare questi diritti, scrivere a: <a href="mailto:privacy@unazampainparadiso.com" className="text-[var(--color-sage)] hover:underline">privacy@unazampainparadiso.com</a>. Risponderemo entro 30 giorni. È possibile proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[var(--color-sage)] hover:underline">garanteprivacy.it</a>).</p>
            </Section>

            <Section title="7. Cookie">
              <p>Il portale utilizza cookie tecnici necessari per il funzionamento del servizio. Ulteriori dettagli sono disponibili nella nostra <a href="/cookie-policy" className="text-[var(--color-sage)] hover:underline font-medium">Cookie Policy</a>.</p>
            </Section>

            <Section title="8. Modifiche alla Privacy Policy">
              <p>Il Titolare si riserva il diritto di apportare modifiche alla presente informativa. Le modifiche sostanziali saranno comunicate via e-mail agli utenti registrati. L'utilizzo continuato del portale dopo la pubblicazione delle modifiche costituisce accettazione delle stesse.</p>
            </Section>

          </div>
        </div>
      </section>
    </div>
  );
}
